import Box from '@mui/material/Box';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { isStatesAndProvinces } from 'api/responses/models/Configuration/StatesAndProvincesDto';
import { ControlledTextField } from 'library/components/controls/form/ControlledTextField';
import { MAX_TEXT_LENGTH } from 'library/constants/Validations';
import { ProductCenterCountryInput } from 'modules/EndCustomer/Shared/ProductCenterCountryInput';
import {
	StatesAndProvincesInput,
	StatesAndProvincesInputCustomProps,
} from 'modules/EndCustomer/Shared/StatesAndProvincesInput';

import { OperatingLocationFormType } from './OperatingLocationForm';

export const emptyOperatingLocation: OperatingLocationFormType = {
	country: null,
	state: null,
	city: null,
	machineVesselLocation: null,
	zipCode: null,
	address: null,
};

export const OperatingLocationFormContent = () => {
	const { t } = useTranslation(['common']);
	const { control } = useFormContext();
	const country = useWatch({ control, name: 'country' });

	const validation = useMemo(
		() => ({
			maxLength: {
				value: MAX_TEXT_LENGTH,
				message: t('common:validation-max-length', {
					length: MAX_TEXT_LENGTH,
				}),
			},
		}),
		[]
	);

	const isDefinedStatesAndProvinces = isStatesAndProvinces(country);

	const StateInput = useMemo(
		() =>
			isDefinedStatesAndProvinces
				? StatesAndProvincesInput
				: ControlledTextField,
		[isDefinedStatesAndProvinces]
	);
	const additionalProps = useMemo(
		() =>
			isDefinedStatesAndProvinces
				? {
						countryCodeWithStatesAndProvinces: country,
					}
				: ({} as StatesAndProvincesInputCustomProps),
		[isDefinedStatesAndProvinces, country]
	);

	return (
		<Box
			sx={{
				display: 'grid',

				gridTemplateColumns: {
					xs: 'repeat(1, 1fr)',
					sm: 'repeat(3, 1fr)',
				},

				gap: 2,
				paddingTop: 1,
			}}>
			<ProductCenterCountryInput
				id="country-input"
				label={t('common:country')}
				name="country"
				validation={{ required: t('validation-error-message') }}
			/>
			<StateInput
				id="state-input"
				label={t('common:county')}
				name="state"
				control={control}
				validation={validation}
				{...additionalProps}
			/>
			<ControlledTextField
				id="city-input"
				label={t('common:city')}
				name="city"
				control={control}
				validation={validation}
			/>
			<ControlledTextField
				id="machine-location-input"
				label={t('common:machine-location')}
				name="machineVesselLocation"
				sx={{ gridColumn: { sm: 'span 3' } }}
				control={control}
				validation={validation}
			/>
			<ControlledTextField
				id="zip-code-input"
				label={t('common:zip-code')}
				name="zipCode"
				control={control}
				validation={validation}
			/>
			<ControlledTextField
				id="address-input"
				label={t('common:address')}
				name="address"
				sx={{ gridColumn: { sm: 'span 2' } }}
				control={control}
				validation={validation}
			/>
		</Box>
	);
};
